import React, { useState } from "react";
import { Row, Col, Spin, Form, Card, Select, InputNumber } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import MainLayout from "../../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import cnt from "../../../../services/definedConstant";
import { rightAction } from "../../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../../treasuryContractBooking.config";
import { BOM } from "../../../../services/definedMethod";

// ── Mock portfolios — replace with real API call when endpoint is ready ───
// BOM.FetchReqAction({ session }, apiConfig.GetPortfolios, (err, res) => {
//   if (!err) setPortfolioOptions(res.map((p) => ({ label: p.name, value: p.id })));
// });
const MOCK_PORTFOLIO_OPTIONS = [
  { label: "Fixed Income Portfolio (ID: 1)", value: 1 },
  { label: "Money Market Portfolio (ID: 2)", value: 2 },
  { label: "Bond Portfolio (ID: 3)", value: 3 },
  { label: "Treasury Portfolio (ID: 4)", value: 4 },
];
// ─────────────────────────────────────────────────────────────────────────

const CreateAuctionBid = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const auctionId = location.state; // passed via navigate("/create-auction-bid", { state: auctionId })
  const { userData } = props;
  const { session } = userData || {};

  const onFinish = (values) => {
    const body = {
      method: "POST",
      portfolioId: values.portfolioId,
      bidAmount: values.bidAmount,
      bidPrice: values.bidPrice,
      bidYield: values.bidYield,
      session,
    };

    const url = `${apiConfig.GetAuctions}/auctions/${auctionId}/bids`;

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Failed to create auction bid.",
        );
        setIsLoading(false);
        return;
      }
      BOM.AlertMsg(cnt.SUCCESS, "Auction bid created successfully.");
      setIsLoading(false);
      navigate(-1);
    });
  };

  const Tip = ({ text, tip }) => (
    <span>
      {text}
      <Tooltip title={tip}>
        <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
      </Tooltip>
    </span>
  );

  return (
    <MainLayout pageItems={staticConfig.newAuction}>
      <TopContent pageTitle={labelConfig.auction} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
              Create Auction Bid
            </h3>

            <Form layout="vertical" form={form} onFinish={onFinish}>
              {/* Portfolio & Bid Amount */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Portfolio"
                        tip="The portfolio placing this auction bid."
                      />
                    }
                    name="portfolioId"
                    rules={[
                      { required: true, message: "Portfolio is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={MOCK_PORTFOLIO_OPTIONS}
                      placeholder="Select Portfolio"
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Bid Amount"
                        tip="The face value amount being bid at this auction."
                      />
                    }
                    name="bidAmount"
                    rules={[
                      { required: true, message: "Bid amount is required" },
                      {
                        type: "number",
                        min: 1,
                        message: "Bid amount must be greater than 0",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 50,000,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Bid Price & Bid Yield */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Bid Price"
                        tip="The price per 100 face value at which the bid is placed."
                      />
                    }
                    name="bidPrice"
                    rules={[
                      { required: true, message: "Bid price is required" },
                      {
                        type: "number",
                        min: 0,
                        max: 100,
                        message: "Bid price must be between 0 and 100",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 98.50"
                      precision={2}
                      min={0}
                      max={100}
                    />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Bid Yield (%)"
                        tip="The annualised yield rate at which the bid is placed."
                      />
                    }
                    name="bidYield"
                    rules={[
                      { required: true, message: "Bid yield is required" },
                      {
                        type: "number",
                        min: 0,
                        message: "Bid yield must be 0 or greater",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 12.50"
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      marginRight: 12,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => form.submit()}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #BA24D5",
                      background: "#BA24D5",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Submit
                  </button>
                </Col>
              </Row>

              <button type="submit" hidden />
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(CreateAuctionBid);
